import { Box, Container, Stack } from '@dtx-company/design-system/src'
import { CSSTransition } from 'react-transition-group'
import { CodePreview } from './CodePreview/CodePreview'
import { FC, useEffect } from 'react'
import { FormActionButtons } from './FormActionButtons/FormActionButtons'
import { GeneratorHeading } from './GeneratorHeading/GeneratorHeading'
import { NAV_V2_HEIGHT } from '@dtx-company/true-common/src/constants/layout'
import { ScrollableContainer } from './ScrollableContainer/ScrollableContainer'
import { TRANSITION_DURATION } from './LoggedOutCodeGenerator.constants'
import { selectDesignFirst, selectFirstSectionIsActive } from '../../machines/locg/locgSelectors'
import { setHideFooter } from '@dtx-company/inter-app/src/redux/slices/settings-slice'
import { setShowStaticContent } from '@dtx-company/inter-app/src/redux/slices/homePageSlice'
import { useDispatch } from 'react-redux'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useLoggedOutGeneratorMobileTransitionStyles } from './hooks/useLoggedOutGeneratorMobileTransitionStyles'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'
import dynamic from 'next/dynamic'

const ProgressBarMobile = dynamic(() =>
  import(
    /* webpackChunkName: "locg-progress-bars" */ './GeneratorHeading/ProgressBar/ProgressBarMobile'
  ).then(mod => mod.ProgressBarMobile)
)
const ProgressBarDesktop = dynamic(() =>
  import(
    /* webpackChunkName: "locg-progress-bars" */ './GeneratorHeading/ProgressBar/ProgressBarDesktop'
  ).then(mod => mod.ProgressBarDesktop)
)

export const LoggedOutCodeGenerator: FC = () => {
  const { isMobile } = useScreenSizeBreakpoints()
  const dispatch = useDispatch()
  const { locgService } = useGlobalServices()
  const designFirst = useSelector(locgService, selectDesignFirst)
  const firstSectionIsActive = useSelector(locgService, selectFirstSectionIsActive)
  const displayFormActionButtons = useSelector(locgService, state => !state.matches('auth'))
  const displayCodePreview = isMobile ? displayFormActionButtons : true
  const displayGeneratorHeading = !isMobile || firstSectionIsActive

  useEffect(() => {
    dispatch(setHideFooter(isMobile))
    dispatch(setShowStaticContent(!isMobile))

    return () => {
      dispatch(setShowStaticContent(true))
      dispatch(setHideFooter(false))
    }
  }, [isMobile, dispatch])

  const { generatorHeadingRef, generatorHeadingTransitionsSx, codePreviewTransitionsSx } =
    useLoggedOutGeneratorMobileTransitionStyles()

  return (
    <Container maxWidth="xl" disableGutters>
      <Stack
        sx={theme => ({
          height: `calc(100vh - ${NAV_V2_HEIGHT}px) `,
          width: '100%',
          borderBottom: {
            md: `1px solid ${theme.palette.border.outline}`,
            lg: 'unset'
          },
          maxHeight: {
            md: theme.spacing(209)
          },
          minHeight: {
            md: theme.spacing(150)
          },
          ...generatorHeadingTransitionsSx
        })}
      >
        <CSSTransition
          in={displayGeneratorHeading}
          timeout={TRANSITION_DURATION}
          classNames="generator-heading"
        >
          <GeneratorHeading ref={generatorHeadingRef} />
        </CSSTransition>

        {!designFirst && <ProgressBarDesktop />}

        <Stack
          direction={{
            xs: 'column',
            md: 'row'
          }}
          sx={{
            width: '100%',
            flex: 1,
            overflow: 'hidden',
            ...codePreviewTransitionsSx
          }}
        >
          <CSSTransition
            in={displayCodePreview}
            timeout={TRANSITION_DURATION}
            classNames="code-preview"
          >
            <Box
              sx={{
                flex: {
                  md: 0.85,
                  lg: 1
                }
              }}
            >
              <CodePreview />
              {!designFirst && <ProgressBarMobile />}
            </Box>
          </CSSTransition>

          <Box
            sx={{
              position: 'relative',
              flex: {
                xs: 1,
                md: 1.5,
                lg: 1
              },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              overflow: 'hidden',
              height: '100%'
            }}
          >
            <ScrollableContainer />
            {displayFormActionButtons && <FormActionButtons />}
          </Box>
        </Stack>
      </Stack>
    </Container>
  )
}
